import React from "react";
import styled from "styled-components";
import { useTranslation } from "@coworker/locales";
import RectangleHorizontalThin from "../../../assets/svg/rectangle-horizontal-thin.svg?react";
import ExclamationSignIcon from "@coworker/reusable/svg/priority-exclamation-sign.svg?react";
import { Stack, StackVertical } from "../CommonComponents";
import amountTypeOptions from "@coworker/enums/amountTypes";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import { NativeNumberKeyPad } from "../NativeNumberKeyPad";
import { RegexOptions } from "../shared/RegexOptions";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ArrowRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ArrowRightIconPath";
import { AmountMaxThresholdMessage } from "@coworker/app/src/components/InputPopup/AmountPopup/AmountMaxThresholdMessage";
import { QUANTITY_MAX_THRESHOLD_PIECES } from "@coworker/apprestructured/src/shared/constants/addon";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

const { PALLET_SETTINGS, MULTI_PACK_SETTINGS, MULTI_PACK, PALLET, PIECES } =
  amountTypeOptions;

const isSettingsTab = (active) =>
  [MULTI_PACK_SETTINGS, PALLET_SETTINGS].includes(active);

const GoToSettings = styled("div")`
  text-decoration: underline;
  color: var(--related-blue) !important;
  height: 52px;
  font-size: 16px;
  line-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ThresholdMessageContainer = styled("div")`
  margin-bottom: 8px;
`;

const WarningMessage = styled("div")`
  font-size: 12px;
  padding-bottom: 16px;
`;

const WarningIcon = styled(ExclamationSignIcon)`
  margin-right: 20px;
  margin-bottom: -6px;
  color: var(--orange);
`;

const ContinueButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-right: 9px;
`;

function AmountTypeIcon({ selectedAmountType }) {
  switch (selectedAmountType) {
    case MULTI_PACK_SETTINGS:
    case MULTI_PACK:
      return (
        <StackVertical>
          <RectangleHorizontalThin />
          <RectangleHorizontalThin />
        </StackVertical>
      );
    case PALLET_SETTINGS:
    case PALLET:
      // Not using Icon component for RectangleHorizontalThin, since it has some custom styles in SVG
      return (
        <StackVertical>
          <RectangleHorizontalThin />
          <RectangleHorizontalThin />
          <RectangleHorizontalThin />
        </StackVertical>
      );
    case PIECES:
    default:
      return (
        <StackVertical>
          <RectangleHorizontalThin />
        </StackVertical>
      );
  }
}

export function AmountPopupMultiPack({
  maxAmount,
  oldAmountSettings,
  goToSettings,
  activeTab,
  inputValue,
  helperText,
  totalPieces,
  maxAmountExceeded,
  availableStock,
  isFoodArticle,
  onComplete,
  setValue,
}) {
  const { t } = useTranslation();
  const containerRef = React.useRef(null);
  const enablePalletMultiPack = useFlag(
    FLAGS.ENABLE_PALLET_MULTIPACK_DATA_SERVICE
  );
  const selectedAmountType = activeTab;
  const getSetting = (type) => {
    if (type === MULTI_PACK) {
      return oldAmountSettings?.pieces_per_multi_pack;
    }
    if (type === PALLET) {
      return oldAmountSettings?.pieces_per_pallet;
    }
  };
  const hasSetting = (type) => getSetting(type) > 0;

  const isPackOrPallet = [MULTI_PACK, PALLET].includes(selectedAmountType);

  const amountTypeButtons = [
    { text: t("piecesUnit"), value: PIECES, "data-testid": PIECES },
    { text: t("multiPackUnit"), value: MULTI_PACK, "data-testid": MULTI_PACK },
    { text: t("palletUnit"), value: PALLET, "data-testid": PALLET },
    {
      text: t("multipackSettingsUnit"),
      value: MULTI_PACK_SETTINGS,
      "data-testid": MULTI_PACK_SETTINGS,
    },
    {
      text: t("palletSettingsUnit"),
      value: PALLET_SETTINGS,
      "data-testid": PALLET_SETTINGS,
    },
  ];

  const formatSuffix = (type) => {
    return [MULTI_PACK, PALLET].includes(type) && !hasSetting(type)
      ? ""
      : amountTypeButtons.find(({ value }) => value === type)?.text;
  };
  const displayWarningMessage = () => {
    if (!totalPieces) {
      return "";
    } else {
      if (isSettingsTab(selectedAmountType)) {
        if (totalPieces > QUANTITY_MAX_THRESHOLD_PIECES) {
          return <AmountMaxThresholdMessage />;
        }
        return "";
      }

      if (hasSetting(selectedAmountType) || !isPackOrPallet) {
        if (totalPieces > QUANTITY_MAX_THRESHOLD_PIECES) {
          return (
            <ThresholdMessageContainer>
              <AmountMaxThresholdMessage />
            </ThresholdMessageContainer>
          );
        } else if (
          maxAmount &&
          totalPieces > maxAmount &&
          totalPieces <= QUANTITY_MAX_THRESHOLD_PIECES
        ) {
          if (maxAmountExceeded) maxAmountExceeded();
          return (
            <WarningMessage>
              <WarningIcon />
              {t("exceedingMaxPiecesString")}
            </WarningMessage>
          );
        }
      }
    }

    return "";
  };
  const showStockWarning =
    !isSettingsTab(selectedAmountType) &&
    availableStock < totalPieces &&
    !isFoodArticle;

  const disabled =
    inputValue === "0" ||
    inputValue === "" ||
    inputValue === undefined ||
    displayWarningMessage();

  const showContinueButton =
    isSettingsTab(selectedAmountType) ||
    !(isPackOrPallet && !hasSetting(selectedAmountType));

  return (
    <>
      <div ref={containerRef}>
        <Stack
          onClick={() => {
            if (!hasSetting(selectedAmountType)) {
              containerRef.current.dispatchEvent(
                new KeyboardEvent("keydown", { key: "ArrowRight" })
              );
            }
          }}
        >
          {!enablePalletMultiPack &&
          isPackOrPallet &&
          !hasSetting(selectedAmountType) ? (
            <GoToSettings data-testid="settingsWarning" onClick={goToSettings}>
              {t("goToSettingsString")}
            </GoToSettings>
          ) : (
            <FixaFormField fieldHelper={{ msg: helperText }}>
              <NativeNumberKeyPad
                StyledInputField
                keypadType="number"
                inputMode="numeric"
                onComplete={() => onComplete(inputValue)}
                onUpdate={(updatedValue) => setValue(updatedValue)}
                initialValue={inputValue}
                regexOptions={RegexOptions.ONLYNUMBERS}
                suffixLabel={formatSuffix(selectedAmountType)}
                prefixLabel={
                  <AmountTypeIcon selectedAmountType={selectedAmountType} />
                }
                disableSubmit={disabled}
              />
            </FixaFormField>
          )}
        </Stack>
      </div>
      {showStockWarning && (
        <WarningMessage>
          <WarningIcon />
          {t("exceedingAvailableStockString")}
        </WarningMessage>
      )}
      {displayWarningMessage()}
      {showContinueButton && (
        <ContinueButtonWrapper>
          <FixaButton
            onClick={() => onComplete(inputValue)}
            ssrIcon={ArrowRightIconPath}
            iconOnly
            disabled={disabled}
            type="emphasised"
            size="small"
          />
        </ContinueButtonWrapper>
      )}
    </>
  );
}
